import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for pagination functionality
 * @param {Function} fetchFunction - Function to fetch data
 * @param {Object} initialParams - Initial parameters
 * @returns {Object} - Pagination state and functions
 */
export function usePagination(fetchFunction, initialParams = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });
  
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...initialParams
  });

  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchFunction(params);

      if (requestId !== requestIdRef.current) return;
      
      if (response && response.items) {
        setData(response.items);
        setPagination(response.pagination || {});
      } else {
        setData(Array.isArray(response) ? response : []);
        setPagination({
          current_page: 1,
          per_page: Array.isArray(response) ? response.length : 0,
          total_items: Array.isArray(response) ? response.length : 0,
          total_pages: 1,
          has_next: false,
          has_previous: false
        });
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Pagination fetch error:', err);
      setError(err.message || 'Failed to fetch data');
      setData([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fetchFunction, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPage = useCallback((page) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const goToNextPage = useCallback(() => {
    if (pagination.has_next) {
      goToPage(pagination.current_page + 1);
    }
  }, [pagination.has_next, pagination.current_page, goToPage]);

  const goToPreviousPage = useCallback(() => {
    if (pagination.has_previous) {
      goToPage(pagination.current_page - 1);
    }
  }, [pagination.has_previous, pagination.current_page, goToPage]);

  const changePageSize = useCallback((limit) => {
    setParams(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  const updateParams = useCallback((newParams) => {
    setParams(prev => ({ ...prev, ...newParams, page: 1 }));
  }, []);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    pagination,
    params,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    changePageSize,
    updateParams,
    refresh,
    isEmpty: data.length === 0 && !loading,
    hasData: data.length > 0,
    isFirstPage: pagination.current_page === 1,
    isLastPage: !pagination.has_next
  };
}

export default usePagination;
